import type { Project, Session } from '@/api/client';
import { getSessionReaderIds } from '@/utils/projectRecordingSessions';
import { readerAssignedCorrections } from '@/utils/readerAssignedCorrections';
import { readerPageSpan } from './readerPages';

/*
 * Внесение запланированной сессии звукорежиссёром (концепт session-popup 1.1.3.2): время, студия и страница остановки
 * или число записанных правок. Правила — как у пошагового подтверждения в старом окне (SessionConfirmField).
 */

/** Шаг времени, минут */
export const RECORD_TIME_STEP = 5;
/** Рабочий день расписания: 09:00–21:00 (validateSessionTimes) */
export const RECORD_DAY_START = 9 * 60;
export const RECORD_DAY_END = 21 * 60;
/** Студии записи; монтажные (5, 6) — не для записи */
export const RECORD_STUDIO_MIN = 1;
export const RECORD_STUDIO_MAX = 4;

/** Что вносится последним: страница остановки (запись) или сколько правок записано (правки); у подкаста — ничего */
export type RecordAmountKind = 'pages' | 'corrections';

export interface RecordAmount {
  kind: RecordAmountKind;
  /** Наименьшее допустимое число: следующая страница после прошлой остановки или 1 правка */
  min: number;
  /** Страниц в книге или правок у чтеца; null — не указано, тогда нет ни «из N», ни «Все» */
  max: number | null;
}

export interface RecordDraft {
  /** Минуты от полуночи */
  start: number;
  end: number;
  studio: number;
  /** Страница или число правок, как набрано (только цифры) */
  amount: string;
}

export type RecordField = 'time' | 'amount';

export interface RecordError {
  field: RecordField;
  /** Текст тоста */
  message: string;
}

type ProjectForRecord = Pick<Project, 'readers' | 'pages' | 'corrections_number'>;

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

function parseMinutes(time: string | null | undefined, fallback: number): number {
  const m = /^(\d{1,2}):(\d{2})/.exec(time ?? '');
  return m ? Number(m[1]) * 60 + Number(m[2]) : fallback;
}

export function formatRecordTime(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

function parseStudio(studio: Session['studio']): number {
  const n = parseInt(String(studio ?? ''), 10);
  return n >= RECORD_STUDIO_MIN && n <= RECORD_STUDIO_MAX ? n : RECORD_STUDIO_MIN;
}

export function isPodcastSession(s: Pick<Session, 'session_type' | 'session_subtype'>): boolean {
  return (s.session_type ?? 'запись') === 'запись' && s.session_subtype === 'podcast';
}

/** Последняя прочитанная страница чтеца: из проекта, иначе — наибольшая страница его записанных сессий (как в старом окне). */
export function lastReadPage(session: Session, project: ProjectForRecord, sessions: readonly Session[]): number {
  const readerId = getSessionReaderIds(session)[0];
  if (!readerId) return 0;
  const fromReader = project.readers?.find((r) => r.id === readerId)?.pages_read;
  if (fromReader != null && fromReader > 0) return fromReader;
  const pages = sessions
    .filter((s) => s.is_recorded && !s.is_canceled && s.stop_page != null && getSessionReaderIds(s).includes(readerId))
    .map((s) => s.stop_page ?? 0);
  return pages.length ? Math.max(...pages) : 0;
}

/** Правок у чтеца сессии (единое правило readerAssignedCorrections); 0 и «не указано» — без верхней границы. */
function correctionsTotal(session: Session, project: ProjectForRecord): number | null {
  const readers = project.readers ?? [];
  const readerId = getSessionReaderIds(session)[0];
  const total = readerAssignedCorrections({
    readerEditsNumber: readers.find((r) => r.id === readerId)?.reader_edits_number,
    projectCorrectionsNumber: project.corrections_number,
    readersCount: readers.length,
  });
  return total != null && total > 0 ? total : null;
}

export function recordAmount(session: Session, project: ProjectForRecord, sessions: readonly Session[]): RecordAmount | null {
  if (session.session_type === 'правки') return { kind: 'corrections', min: 1, max: correctionsTotal(session, project) };
  if (isPodcastSession(session)) return null;
  // Страницы чтеца (своё число или диапазон) перебивают страницы книги: «Все» — его последняя страница, раньше начала диапазона — нельзя
  const reader = project.readers?.find((r) => r.id === getSessionReaderIds(session)[0]);
  const span = readerPageSpan(reader ?? {}, project.pages);
  return { kind: 'pages', min: Math.max(span?.from ?? 1, lastReadPage(session, project, sessions) + 1), max: span?.to ?? null };
}

/** Черновик из плана сессии; уже внесённое число — в поле. */
export function initialRecordDraft(session: Session): RecordDraft {
  const start = parseMinutes(session.start, 10 * 60);
  const amount = session.session_type === 'правки' ? session.corrections_recorded : session.stop_page;
  return {
    start,
    end: parseMinutes(session.end, start + 3 * 60),
    studio: parseStudio(session.studio),
    amount: amount != null ? String(amount) : '',
  };
}

export function stepRecordTime(minutes: number, dir: 1 | -1): number {
  return clamp(minutes + dir * RECORD_TIME_STEP, RECORD_DAY_START, RECORD_DAY_END);
}

export function stepRecordStudio(studio: number, dir: 1 | -1): number {
  return clamp(studio + dir, RECORD_STUDIO_MIN, RECORD_STUDIO_MAX);
}

/** Есть что вносить: страница или число правок набраны (или «Все»); у подкаста вносить нечего — готово сразу. Иначе «Сохранить» серая */
export function isRecordDraftReady(draft: Pick<RecordDraft, 'amount'>, amount: RecordAmount | null): boolean {
  return amount === null || draft.amount !== '';
}

/** Первая ошибка черновика; null — можно вносить. Сообщение — для тоста, поле — для красной обводки. */
export function validateRecordDraft(draft: RecordDraft, amount: RecordAmount | null): RecordError | null {
  if (draft.end <= draft.start) return { field: 'time', message: 'Конец записи раньше начала' };
  if (!amount) return null;
  const pages = amount.kind === 'pages';
  if (!draft.amount) {
    return { field: 'amount', message: pages ? 'Укажите страницу, на которой остановились' : 'Укажите, сколько правок записано' };
  }
  const n = Number(draft.amount);
  if (n < amount.min) {
    return { field: 'amount', message: pages ? `Страница должна быть больше ${amount.min - 1}` : 'Правок должно быть больше 0' };
  }
  if (amount.max != null && n > amount.max) {
    return { field: 'amount', message: pages ? `Последняя страница — ${amount.max}` : `Правок всего ${amount.max}` };
  }
  return null;
}

/** Тело PATCH /sessions/:id: сессия записана этим звукорежиссёром. Шрифты по длительности пересчитывает сервер. */
export function recordSessionPatch(
  draft: RecordDraft,
  amount: RecordAmount | null,
  soundengineerId: string
): Partial<Session> {
  const n = draft.amount ? Number(draft.amount) : null;
  return {
    start: formatRecordTime(draft.start),
    end: formatRecordTime(draft.end),
    studio: String(draft.studio),
    is_recorded: true,
    is_partial: false,
    soundengineer_id: soundengineerId,
    stop_page: amount?.kind === 'pages' ? n : null,
    ...(amount?.kind === 'corrections' ? { corrections_recorded: n } : {}),
  };
}
